import { useEffect, useRef, useState, KeyboardEvent, FocusEvent } from "react";

import styles from "Pages/Integration/EditIntegrations2/EditIntegrations2.module.css";

interface IPropsCommon {
    rowId: number;
    field:
        | "name"
        | "direction"
        | "disabled"
        | "hidden"
        | "from_system"
        | "to_system";
    onSave: (value: string) => Promise<void>;
}

interface IPropsText extends IPropsCommon {
    kind: "text";
    value: string;
    displayLabel?: string;
}

interface IPropsSelect extends IPropsCommon {
    kind: "select";
    value: string;
    options: { value: string; label: string }[];
    displayLabel?: string;
}

type IProps = IPropsText | IPropsSelect;

/**
 * Click-to-edit cell. Mirrors the Portal2 _InlineCell + _InlineCellEdit
 * pattern: button shows the value, click swaps to input/select with autofocus,
 * Enter saves, Esc restores, blur-with-change saves silently.
 */
export default function InlineEditableCell(props: IProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(props.value);
    const cancelledRef = useRef(false);

    useEffect(() => {
        // Keep the draft in sync if the row gets re-fetched (e.g. after save
        // → reload). Only when not actively editing.
        if (!editing) setDraft(props.value);
    }, [props.value, editing]);

    const display =
        props.displayLabel !== undefined
            ? props.displayLabel
            : props.value === ""
              ? "(none)"
              : props.value;

    if (!editing) {
        return (
            <button
                type="button"
                className={`${styles.inlineCell} ${
                    props.field === "disabled" || props.field === "hidden"
                        ? styles.inlineCellToggle
                        : ""
                }`}
                onClick={() => {
                    cancelledRef.current = false;
                    setDraft(props.value);
                    setEditing(true);
                }}
                title={`Click to edit ${props.field}`}
            >
                {display}
            </button>
        );
    }

    const commit = async (next: string) => {
        if (next === props.value) {
            setEditing(false);
            return;
        }
        await props.onSave(next);
        setEditing(false);
    };

    const onKey = (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (e.key === "Escape") {
            cancelledRef.current = true;
            setDraft(props.value);
            setEditing(false);
        } else if (e.key === "Enter") {
            e.preventDefault();
            commit(draft);
        }
    };

    const onBlur = (e: FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (cancelledRef.current) {
            cancelledRef.current = false;
            return;
        }
        commit(e.currentTarget.value);
    };

    if (props.kind === "text") {
        return (
            <input
                className={styles.inlineCellInput}
                type="text"
                value={draft}
                autoFocus
                onFocus={(e) => e.currentTarget.select()}
                onChange={(e) => setDraft(e.currentTarget.value)}
                onKeyDown={onKey}
                onBlur={onBlur}
                minLength={2}
            />
        );
    }

    return (
        <select
            className={styles.inlineCellInput}
            value={draft}
            autoFocus
            onChange={(e) => {
                setDraft(e.currentTarget.value);
                commit(e.currentTarget.value);
            }}
            onKeyDown={onKey}
            onBlur={onBlur}
        >
            {props.options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
    );
}
